import { isObjectOf, isShapeOf, isUnknown } from 'giltig';

import type { Branded, Mutable, Prettify } from './types';

export type StudioExperience =
  | 'max'
  | 'discoveryplus'
  | 'bleacherreport'
  | 'cnn'
  | 'tntsportsplus'
  | 'shared'
  | Branded<string, 'UnknownStudioExperience'>;

export type SubdivisionTenant =
  | 'beam'
  | 'dplus'
  | 'br'
  | 'cnn'
  | 'tntsportsplus'
  | Branded<string, 'UnknownSubdivisionTenant'>;

export type SubdivisionMarket =
  | 'amer'
  | 'latam'
  | 'emea'
  | 'apac'
  | Branded<string, 'UnknownSubdivisionMarket'>;

export type PublishingSite =
  | 'max_global'
  | 'fallback' // "Max Courtesy"
  | 'beam_us'
  | 'discovery_plus'
  | 'tntsportsplus'
  | 'tntsportsplus_fallback'
  | `dplus_${Lowercase<string>}` // Legacy D+ Publishing Sites
  | Branded<string, 'UnknownPublishingSite'>;

export type ProductLine =
  | 'c01c1c07-e7bf-4cee-be6b-87092a30d41c' // Max
  | '87671121-ed22-4ecb-8158-b5c2bd2472c3' // Discovery+
  | 'd646434d-bcf9-4e40-88fb-c5ca52d8ccf0' // Bleacher Report
  | '01224710-a55c-4c9f-a9b1-6e84139f1d1a' // CNN
  | '898d590a-cde8-4e6b-88de-da1478a8cfe8' // TNT Sports
  | Branded<string, 'UnknownProductLine'>;

export type CommerceLine =
  | '3P1GD1' // Max
  | '2RRCI7' // Discovery+
  | '4Q2HE2' // Bleacher Report
  | '1BH5NN' // CNN
  | 'SQHZW1' // TNT Sports
  | Branded<string, 'UnknownCommerceLine'>;

type Satisfies<T, U extends T> = U;

export type SecondaryExperiences = Satisfies<
  Readonly<Record<SecondaryExperienceKey, string>>,
  Readonly<{
    subdivisionTenant: SubdivisionTenant;
    subdivisionMarket: SubdivisionMarket;
    publishingSite: PublishingSite;
    productLine: ProductLine;
    commerceLine: CommerceLine;
  }>
>;

export const SECONDARY_EXPERIENCE_KEYS = Object.freeze([
  'subdivisionTenant',
  'subdivisionMarket',
  'publishingSite',
  'commerceLine',
  'productLine',
] as const);

export type SecondaryExperienceKey = (typeof SECONDARY_EXPERIENCE_KEYS)[number];

export const EXPERIENCE_KEYS = Object.freeze([
  'studioExperience',
  ...SECONDARY_EXPERIENCE_KEYS,
] as const);

export type ExperienceKey = (typeof EXPERIENCE_KEYS)[number];

export interface PersistedExperience {
  readonly studioExperience: StudioExperience | null;
  readonly secondaryExperiences: {
    readonly [_ in StudioExperience]?: OptionalSecondaryExperiences | undefined;
  };
}

export type ExperienceConfig = {
  readonly [_ in StudioExperience]?: {
    readonly [Key in SecondaryExperienceKey]?: readonly SecondaryExperiences[Key][];
  };
};

export type OptionalSecondaryExperiences = {
  readonly [Key in SecondaryExperienceKey]?:
    | SecondaryExperiences[Key]
    | null
    | undefined;
};

export interface Experience extends OptionalSecondaryExperiences {
  readonly studioExperience?: StudioExperience | null;
}

declare const ELIGIBLE_EXPERIENCE_SYMBOL: unique symbol;

/**
 * An experience that contains all the required secondary experiences,
 * and is allowed according to the experience config
 */
export interface EligibleExperience extends Experience {
  readonly studioExperience: StudioExperience;
  readonly [ELIGIBLE_EXPERIENCE_SYMBOL]: never;
}

declare const AUTHORIZED_EXPERIENCE_SYMBOL: unique symbol;

/**
 * An experience that the user has access to
 */
export interface AuthorizedExperience extends Experience {
  readonly [AUTHORIZED_EXPERIENCE_SYMBOL]: never;
}

export type VerifiedExperience = EligibleExperience & AuthorizedExperience;

const EXPERIENCE_KEYS_LOOSE: readonly string[] = EXPERIENCE_KEYS;

export function isExperienceKey(value: string): value is ExperienceKey {
  return EXPERIENCE_KEYS_LOOSE.includes(value);
}

export const isObjectWithExperience = isShapeOf({
  experience: isObjectOf(isUnknown),
});

export function isMaybeStudioExperience(
  value: unknown,
): value is StudioExperience {
  return typeof value === 'string' && value.length > 0;
}

export function isMaybeSubdivisionTenant(
  value: unknown,
): value is SubdivisionTenant {
  return typeof value === 'string' && value.length > 0;
}

export function isMaybeSubdivisionMarket(
  value: unknown,
): value is SubdivisionMarket {
  return typeof value === 'string' && value.length > 0;
}

export function isMaybePublishingSite(value: unknown): value is PublishingSite {
  return typeof value === 'string' && value.length > 0;
}

export function isMaybeCommerceLine(value: unknown): value is CommerceLine {
  return typeof value === 'string' && value.length > 0;
}

export function isMaybeProductLine(value: unknown): value is ProductLine {
  return typeof value === 'string' && value.length > 0;
}

export interface ExperienceValidationOptions {
  /**
   * The secondary experience keys that are required for the handler to work.
   * Null means that no experiences are needed.
   * Undefined means that the check is not performed.
   */
  readonly experiences?: readonly SecondaryExperienceKey[] | null;
}

export type ValidatedExperience<
  SomeExperience extends Experience | null,
  Options extends ExperienceValidationOptions,
> = Options['experiences'] extends undefined
  ? SomeExperience
  : Options['experiences'] extends null
    ? null
    : Options['experiences'] extends readonly (infer RequiredSecondaryExperienceKey extends
          SecondaryExperienceKey)[]
      ? SomeExperience extends Experience
        ? Prettify<
            Omit<SomeExperience, SecondaryExperienceKey> & {
              readonly [K in RequiredSecondaryExperienceKey]-?: Exclude<
                SomeExperience[K],
                null | undefined
              >;
            }
          >
        : never
      : never;

export function getValidatedExperience<
  const SomeExperience extends Experience | null,
  const Options extends ExperienceValidationOptions,
>(
  experience: SomeExperience,
  options: Options | undefined,
): ValidatedExperience<SomeExperience, Options> {
  // If no experiences are required, return the experience as is (no validation needed)
  if (options?.experiences === undefined) {
    return experience as ValidatedExperience<SomeExperience, Options>;
  }

  // If the required experiences are null, return null
  if (options.experiences === null) {
    return null as ValidatedExperience<SomeExperience, Options>;
  }

  // If we have required experiences, the experience must not be null
  if (experience === null) {
    throw new TypeError('Experience must not be null');
  }

  // Check that the experience contains all the required secondary experiences
  for (const experienceKey of options.experiences) {
    if (typeof experience[experienceKey] !== 'string') {
      throw new TypeError(
        `Experience is missing required key "${experienceKey}"`,
      );
    }
  }

  let modifiedExperience: Mutable<Experience> | undefined;

  // Remove all secondary experiences that are not in the options
  for (const rawExperienceKey in experience) {
    if (Object.hasOwn(experience, rawExperienceKey)) {
      const experienceKey = rawExperienceKey as keyof Experience;
      if (
        experienceKey !== 'studioExperience' &&
        !options.experiences.includes(experienceKey)
      ) {
        modifiedExperience ??= { ...experience };
        delete modifiedExperience[experienceKey];
      }
    }
  }

  return (modifiedExperience ?? experience) as ValidatedExperience<
    SomeExperience,
    Options
  >;
}
